import { CourseType } from "../model/Course";
import { TranslationKey } from "../translations/TranslationKey";

export enum PropertyNames {
  id = "id",
  roleExams = "roleExams",
  firstName = "firstName",
  lastName = "lastName",
  emailAddress = "emailAddress",
  employer = "employer",
  employerInformation = "employerInformation",
  birthday = "birthday",
  blocked = "blocked",
  gender = "gender",
  ahvNumber = "ahvNumber",
  street = "street",
  streetNumber = "streetNumber",
  zipCode = "zipCode",
  city = "city",
  country = "country",
  phoneNumber = "phoneNumber",
  mobileNumber = "mobileNumber",
  ciceroNumber = "ciceroNumber",
  bankAccount = "bankAccount",
  correspondenceLanguage = "correspondenceLanguage",
  languages = "languages",
  birthdate = "birthdate",
  canton = "canton",
  employerCity = "employerCity",
  employerCountry = "employerCountry",
  employerStreet = "employerStreet",
  employerStreetNumber = "employerStreetNumber",
  employerZipCode = "employerZipCode",
  nationalityIsSwiss = "nationalityIsSwiss",
  placeOfOrigin = "placeOfOrigin",
  privateIsPreferredContact = "privateIsPreferredContact",
  numberOfFailedWrittenExams = "numberOfFailedWrittenExams",
  numberOfFailedOralExams = "numberOfFailedOralExams",
  nameDE = "nameDE",
  nameFR = "nameFR",
  nameIT = "nameIT",
  nameEN = "nameEN",
  examId = "examId",
  abacusNumber = "abacusNumber",
  price = "price",
  expensesOralExam = "expensesOralExam",
  expensesWrittenExam = "expensesWrittenExam",
  moduleDetails = "moduleDetails",
  moduleType = "moduleType",
  certificateValidity = "certificateValidity",
  internalComment = "internalComment",
}

export const Switzerland = "Schweiz";
export const SwissGermanLocale = "de-CH";
export const SwissTimeZone = "Europe/Zurich";

export enum Language {
  DE = "DE",
  EN = "EN",
  FR = "FR",
  IT = "IT",
}

export type LanguageKeys = "DE" | "EN" | "FR" | "IT";

export const LanguageLocale: { [id in Language]: string } = {
  [Language.DE]: "de-de",
  [Language.EN]: "en-en",
  [Language.FR]: "fr-fr",
  [Language.IT]: "it-it",
};

export const LanguageLabels: { [id in Language]: string } = {
  [Language.DE]: "Deutsch",
  [Language.EN]: "English",
  [Language.FR]: "Français",
  [Language.IT]: "Italiano",
};

export const LanguageTranslationKeys: { [id in Language]: TranslationKey } = {
  [Language.DE]: "common.language.de",
  [Language.EN]: "common.language.en",
  [Language.FR]: "common.language.fr",
  [Language.IT]: "common.language.it",
};

export interface SelectOption {
  value: string;
  label: string;
}

export interface ContactReference {
  id: string;
  displayName: string;
}

export enum UserGender {
  Male = "Male",
  Female = "Female",
}

export const UserGenderLabels = (key: UserGender): TranslationKey => {
  switch (key) {
    case UserGender.Male: {
      return "common.genders.male";
    }
    case UserGender.Female: {
      return "common.genders.female";
    }
  }
};

export const DateTimeFormat = "DD.MM.YYYY HH:mm";
export const DateFormat = "DD.MM.YYYY";
export const TimeFormat = "HH:mm";

export interface RequiredCandidate {
  isFirstnameEmpty: boolean;
  isLastnameEmpty: boolean;
  isGenderEmpty: boolean;
  isBirthDateEmpty: boolean;
  isEmailEmpty: boolean;
  isStreetEmpty: boolean;
  isStreetNumberEmpty: boolean;
  isZipCodeEmpty: boolean;
  isCityEmpty: boolean;
  isCountryEmpty: boolean;
  isLanguageEmpty: boolean;
  isAhvNoEmpty: boolean;
  isNationalityEmpty: boolean;
  isPlaceOfOriginEmpty: boolean;
  isCantonEmpty: boolean;
  isEmployerInformationEmpty: boolean;
}

export interface RequiredCandidateExpert {
  isBankAccountEmpty: boolean;
  hasEmptyRoleArray: ExpertRole[];
  hasEmptyRole: boolean;
}

export interface RequiredModule {
  isNameDEEmpty: boolean;
  isNameENEmpty: boolean;
  isNameITEmpty: boolean;
  isNameFREmpty: boolean;
}

export interface RequiredCandidateMyProfile {
  isFirstnameEmpty: boolean;
  isLastnameEmpty: boolean;
  isGenderEmpty: boolean;
  isBirthDateEmpty: boolean;
  isStreetEmpty: boolean;
  isStreetNumberEmpty: boolean;
  isZipCodeEmpty: boolean;
  isCityEmpty: boolean;
  isCountryEmpty: boolean;
  isLanguageEmpty: boolean;
  isAhvNoEmpty: boolean;
  isNationalityEmpty: boolean;
  isPlaceOfOriginEmpty: boolean;
  isCantonEmpty: boolean;
  isEmployerInformationEmpty: boolean;
  isEmployerEmpty: boolean;
  isEmployerStreetEmpty: boolean;
  isEmployerStreetNumberEmpty: boolean;
  isEmployerZipCodeEmpty: boolean;
  isEmployerCityEmpty: boolean;
  isEmployerCountryEmpty: boolean;
  isEmailEmpty: boolean;
  isBankAccountEmpty: boolean;
}

export interface ValidationFields {
  isEmailValid: boolean;
  isAhvValid: boolean;
  isCiceroValid: boolean;
  isPhoneValid: boolean;
  isMobileValid: boolean;
}

export interface ContactReference {
  id: string;
  displayName: string;
}

export enum UserRoomAssignmentType {
  OralExam = "OralExam",
  WrittenExam = "WrittenExam",
  OralPreparation = "OralPreparation",
  Correction = "Correction",
}

export enum ExpertRole {
  Prüfungsexperten = "Prüfungsexperten",
  Expertenschulung = "Expertenschulung",
  Hauptexperten = "Hauptexperten",
  Leiterprüfungszentrum = "Leiterprüfungszentrum",
  EinspracheKomission = "EinspracheKomission",
  Prüfungskomission = "Prüfungskomission",
  Aufsichtsexperten = "Aufsichtsexperten",
  QSK = "QSK",
}

export type RoleWithExamsAndCourseTypes = {
  role: ExpertRole;
  examIds: string[];
  courseTypes: CourseType[];
};

export const AllExpertRoles: ExpertRole[] = Object.values(ExpertRole);

export function getExpertRoleTranslationKey(role: ExpertRole): TranslationKey {
  return ("expert.field.role." + role) as TranslationKey;
}

export enum LocalStorageKey {
  LastSelectedLanguageKey = "LastSelectedLangKey",
}

export enum ExpertMinumimFixedUsers {
  "proctorWrittenExam" = 2,
  "proctorOralPreparation" = 1,
  "expertOralExam" = 2,
  "expertCorrectionExam" = 0,
}

export const NewEntityId = "0000000000";

export enum UrlQueryParameters {
  datatransTrxId = "datatransTrxId",
}

export const WeekDaysNumberArray = [0, 1, 2, 3, 4, 5, 6];

